import { Timer } from 'lucide-react';
import { cn } from '@/shared/lib/utils';
import { useSharedTicker } from '../hooks/useSharedTicker';
import { formatDwell } from '../utils/formatters';
import { isTerminalStatus, stateEnteredAtIso } from '../utils/lifecycle';
import type { OrderResponse, OrderStatus } from '../types';

interface Props {
  order: OrderResponse;
  /** Optional prefix label override. Defaults to a status-aware verb. */
  label?: string;
  className?: string;
  /** Compact: drops the prefix label and shows just the icon + time. */
  compact?: boolean;
}

/**
 * Live-ticking dwell pill. Tints by urgency so an owner can triage at a glance:
 * fresh → muted, warming → amber, stale → destructive w/ pulse.
 * No-ops for terminal statuses (COMPLETED / CANCELLED).
 */
export function DwellClock({ order, label, className, compact }: Props) {
  const now = useSharedTicker();
  if (isTerminalStatus(order.orderStatus)) return null;
  const iso = stateEnteredAtIso(order);
  if (!iso) return null;
  const text = formatDwell(now, iso);
  if (!text) return null;

  const elapsedMs = now - Date.parse(iso);
  const urgency = urgencyFor(order.orderStatus, elapsedMs);
  const prefix = label ?? defaultLabel(order.orderStatus);

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-medium leading-none tabular-nums transition-colors',
        URGENCY_STYLES[urgency],
        className
      )}
      aria-label={`${prefix} ${text}`}
      title={`${prefix} ${text}`}
    >
      <span className="relative inline-flex h-3 w-3 shrink-0 items-center justify-center">
        <Timer
          className={cn(
            'h-3 w-3',
            urgency === 'stale' ? 'animate-pulse' : undefined
          )}
        />
        {urgency !== 'fresh' && (
          <span
            aria-hidden
            className={cn(
              'absolute -right-0.5 -top-0.5 inline-flex h-1.5 w-1.5',
              urgency === 'stale' && 'animate-ping'
            )}
          >
            <span
              className={cn(
                'inline-flex h-full w-full rounded-full',
                urgency === 'warm' ? 'bg-amber-500' : 'bg-destructive'
              )}
            />
          </span>
        )}
      </span>
      {!compact && <span className="opacity-80">{prefix}</span>}
      <span className="font-semibold">{text}</span>
    </span>
  );
}

type Urgency = 'fresh' | 'warm' | 'stale';

const URGENCY_STYLES: Record<Urgency, string> = {
  fresh: 'border-border/60 bg-muted/60 text-muted-foreground',
  warm: 'border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400',
  stale: 'border-destructive/40 bg-destructive/10 text-destructive',
};

// Minutes until a given status is considered warm / stale. Tuned so PENDING
// goes red fastest (owners need to accept), while later stages allow more
// headroom for actual kitchen/delivery work.
const THRESHOLDS_MIN: Record<OrderStatus, { warm: number; stale: number }> = {
  PENDING: { warm: 2, stale: 5 },
  SCHEDULED: { warm: Infinity, stale: Infinity },
  CONFIRMED: { warm: 5, stale: 15 },
  PREPARING: { warm: 15, stale: 30 },
  IN_DELIVERY: { warm: 20, stale: 45 },
  READY_FOR_PICKUP: { warm: 10, stale: 25 },
  COMPLETED: { warm: Infinity, stale: Infinity },
  CANCELLED: { warm: Infinity, stale: Infinity },
};

function urgencyFor(status: OrderStatus, elapsedMs: number): Urgency {
  const { warm, stale } = THRESHOLDS_MIN[status];
  const min = elapsedMs / 60_000;
  if (min >= stale) return 'stale';
  if (min >= warm) return 'warm';
  return 'fresh';
}

function defaultLabel(status: OrderStatus): string {
  switch (status) {
    case 'PENDING':
      return 'Waiting';
    case 'CONFIRMED':
      return 'Confirmed';
    case 'PREPARING':
      return 'Preparing';
    case 'IN_DELIVERY':
      return 'Out';
    case 'READY_FOR_PICKUP':
      return 'Ready';
    default:
      return 'In state';
  }
}
